import { z } from "zod";

/**
 * What `GET /auth/identity-home` answers for an address that names an identity
 * home (docs/adr/33-identity-homes-sign-in-chosen-by-the-address.md): the
 * Organisation's name, which its sign-in page is titled with, and the GCIP
 * tenant its members' Identities live in, which the page signs them into.
 *
 * Nothing else. The record's *No probing* bounds this: an address that names no
 * home, a switched-off one, or one whose pool is not made yet are one answer and
 * the same, a 404, so the body never becomes a way to enumerate Organisations.
 *
 * Shared rather than declared twice so the client CHECKS what it received
 * instead of trusting it, as the constitution article "Data from outside is
 * checked before use" asks — a client's own server is still outside.
 * Deliberately not `strictObject`: unknown keys are stripped rather than
 * refused, so a later field added on the server does not refuse the answer in
 * clients already in people's browsers.
 */
export const IdentityHomeAnswerSchema = z.object({
	displayName: z.string().min(1),
	gcipTenantId: z.string().min(1),
});

export type IdentityHomeAnswer = z.infer<typeof IdentityHomeAnswerSchema>;

/**
 * What `GET /auth/sign-in-address` answers for a label somebody can sign in
 * under (TECH-3353): the address of that identity home's sign-in page. The
 * find-your-Organisation page goes there without reading it, so the client
 * learns nothing about where a label sits in an address.
 *
 * An http(s) URL and nothing else, because the page navigates to it: an answer
 * that is not one is refused rather than followed, and a `javascript:` URL in
 * particular can never become a script the browser runs.
 */
export const SignInAddressAnswerSchema = z.object({
	address: z.url({ protocol: /^https?$/ }),
});

export type SignInAddressAnswer = z.infer<typeof SignInAddressAnswerSchema>;
